import React, { useContext } from 'react';

import { isEmpty } from 'lodash';

import { DownloadAccess, UserAccessRestrictions } from '/types';
import { AppShellContext, UserAccessRestrictionContext } from '/utils/contexts';

const useUserAccessRestrictions = (): UserAccessRestrictions => {
  const { accessDetails, userDetails } = useContext(AppShellContext);
  const mntl = accessDetails?.mntl;

  if (!mntl) {
    return {
      downloads: {
        fullReport: false,
        myPresentation: false,
        executiveSummary: false,
        infographicOverview: false,
        assetBlock: false,
        chartData: false,
        tableData: false,
      },
      isAcademicUser: false,
      isAnonymousUser: true,
      isLibraryLockdownUser: false,
      isMasquerading: false,
      isLoggedInUser: accessDetails !== undefined,
      toolsDisabled: true,
    };
  }

  const userDownloadAccess =
    mntl.access?.clients?.downloads ||
    (mntl.access.gnpd?.disable_downloads === 1
      ? DownloadAccess.None
      : DownloadAccess.All);

  const allOrLimitedDownload = [
    DownloadAccess.All,
    DownloadAccess.Limited,
  ].includes(userDownloadAccess);

  const numProducts = Object.values(mntl.access).filter(
    (access) => !isEmpty(access),
  ).length;

  return {
    downloads: {
      fullReport: userDownloadAccess === DownloadAccess.All,
      myPresentation: allOrLimitedDownload,
      executiveSummary: allOrLimitedDownload,
      infographicOverview: allOrLimitedDownload,
      assetBlock: allOrLimitedDownload,
      chartData: allOrLimitedDownload,
      tableData: allOrLimitedDownload,
    },
    isAcademicUser: !!mntl.is_academic,
    isAnonymousUser: !!mntl.anon,
    isLibraryLockdownUser: !!mntl.access?.oxygen?.library_lockdown,
    isMasquerading:
      !!userDetails?.email && !!mntl.mail && userDetails.email !== mntl.mail,
    isLoggedInUser: accessDetails !== undefined,
    toolsDisabled:
      !!mntl.anon ||
      (numProducts === 1 &&
        (mntl.access?.clients?.verticals || []).length === 0 &&
        (mntl.access?.clients?.report_subscriptions || []).length === 0 &&
        (mntl.access?.clients?.single_copy || []).length > 0),
  };
};

const UserAccessRestrictionProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const accessRestrictions = useUserAccessRestrictions();

  return (
    <UserAccessRestrictionContext.Provider value={accessRestrictions}>
      {children}
    </UserAccessRestrictionContext.Provider>
  );
};

export default UserAccessRestrictionProvider;
